import { current, isDraft } from "@reduxjs/toolkit";
import type { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { ablyChannelNamesForSession } from "_shared/_stores/realtime/ably-channel-names";
import type { RootState } from "_shared/_stores/root-reducer";
import reduxAblyMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-ably-middleware";
import type { AppDispatch } from "_shared/_stores/store-types";
import Guid from "_shared/guids/guid";
import type { IndividualJSON } from "marketplace/individuals/_schema/individual-schema";
import Session from "users/__session/session";
import UserInformationSchema, { type UserInformationJSON } from "users/information/_schemas/user-information-schema";
import UserProfileSchema, { type UserProfileJSON } from "users/information/profile-information/user-profile-schema";
import {
  forgetNetworkConnectionStatus,
  removeNetworkConnection,
  upsertNetworkConnection,
} from "users/networking/_stores/network-connections-redux-slice";
import { removeNetworkInvitation, upsertNetworkInvitation } from "users/networking/_stores/network-invitations-redux-slice";
import type NetworkConnectionQuery from "users/networking/connections/_api/queries/network-connection-query";
import {
  type NetworkConnectionListArgs,
  userNetworkConnectionsApi,
} from "users/networking/connections/_api/user-network-connection-api";
import { userNetworkInvitationApi } from "users/networking/connections/invitations/_api/network-invitation-api";
import {
  type default as NetworkInvitationQuery,
  NetworkInvitationDirection,
} from "users/networking/connections/invitations/_api/queries/network-invitation-query";
import NetworkInvitationInfoSchema, {
  type NetworkInvitationInfoJSON,
} from "users/networking/connections/invitations/_schemas/network-invitation-info-schema";

type NetworkConnectionTag = { type: "UserNetworkConnection"; id: string };
type NetworkInvitationTag = { type: "NetworkInvitation"; id: string };
type QueryCacheEntry = {
  endpointName: string;
  originalArgs: unknown;
};

const getIdValue = (value: unknown): string | undefined => {
  if (typeof value === "string" && Guid.isValid(value)) return value;

  if (value && typeof value === "object" && "value" in value) {
    const nestedValue = (value as { value?: unknown }).value;
    if (typeof nestedValue === "string" && Guid.isValid(nestedValue)) return nestedValue;
  }

  return undefined;
};

const getPayloadId = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== "object") return undefined;
  return getIdValue((payload as { id?: unknown }).id);
};

const getPayloadUserId = (payload: unknown, key: "userA" | "userB" | "inviter" | "invitee"): string | undefined => {
  if (!payload || typeof payload !== "object") return undefined;
  const user = (payload as Record<string, unknown>)[key];
  if (!user || typeof user !== "object") return undefined;
  return getIdValue((user as { id?: unknown }).id);
};

const getRelatedUserId = (
  payload: unknown,
  firstKey: "userA" | "inviter",
  secondKey: "userB" | "invitee",
): string | undefined => {
  const currentUserId = Session.loadFromStorage(() => {}).user?.id?.value;
  const firstUserId = getPayloadUserId(payload, firstKey);
  const secondUserId = getPayloadUserId(payload, secondKey);

  if (currentUserId === firstUserId) return secondUserId;
  if (currentUserId === secondUserId) return firstUserId;

  return firstUserId ?? secondUserId;
};

const buildNetworkConnectionTags = (payload: unknown): NetworkConnectionTag[] => {
  const tagIds = new Set<string>(["LIST"]);
  const connectionId = getPayloadId(payload);
  const relatedUserId = getRelatedUserId(payload, "userA", "userB");

  if (connectionId) tagIds.add(connectionId);
  if (relatedUserId) tagIds.add(relatedUserId);

  return [...tagIds].map((id) => ({ type: "UserNetworkConnection", id }));
};

const buildNetworkConnectionTagsFromInvitation = (payload: unknown): NetworkConnectionTag[] => {
  const tagIds = new Set<string>(["LIST"]);
  const relatedUserId = getRelatedUserId(payload, "inviter", "invitee");

  if (relatedUserId) tagIds.add(relatedUserId);

  return [...tagIds].map((id) => ({ type: "UserNetworkConnection", id }));
};

const buildNetworkInvitationTags = (payload: unknown): NetworkInvitationTag[] => {
  const tagIds = new Set<string>(["LIST"]);
  const invitationId = getPayloadId(payload);

  if (invitationId) tagIds.add(invitationId);

  return [...tagIds].map((id) => ({ type: "NetworkInvitation", id }));
};

const getPayloadUserProfile = (
  payload: unknown,
  key: "userA" | "userB" | "inviter" | "invitee",
): UserProfileJSON | undefined => {
  if (!payload || typeof payload !== "object") return undefined;
  const user = (payload as Record<string, unknown>)[key];
  return toUserProfileJSON(user);
};

const toUserProfileJSON = (payload: unknown): UserProfileJSON | undefined => {
  const parsed = UserProfileSchema.UserProfileJSONZ.safeParse(payload);
  return parsed.success ? parsed.data : undefined;
};

const toUserInformationJSON = (payload: unknown): UserInformationJSON | undefined => {
  const parsed = UserInformationSchema.UserInformationJSONZ.safeParse(payload);
  return parsed.success ? parsed.data : undefined;
};

const toNetworkInvitationInfoJSON = (payload: unknown): NetworkInvitationInfoJSON | undefined => {
  const parsed = NetworkInvitationInfoSchema.NetworkInvitationInfoJSONZ.safeParse(payload);
  return parsed.success ? parsed.data : undefined;
};

const getCachedQueryEntries = (
  getState: () => RootState,
  tags: NetworkConnectionTag[] | NetworkInvitationTag[],
  endpointName: string,
): QueryCacheEntry[] =>
  userNetworkConnectionsApi.util
    .selectInvalidatedBy(getState(), tags)
    .filter((entry) => entry.endpointName === endpointName);

const getIndividualByUserId = (state: RootState, userId?: string): IndividualJSON | undefined => {
  if (!userId) return undefined;
  return state.individuals.byUserId.entries[userId];
};

const textIncludes = (value: string | null | undefined, query: string): boolean =>
  value?.toLowerCase().includes(query.toLowerCase().trim()) ?? false;

const getTotalPages = (totalElements: number, pageSize: number): number =>
  pageSize > 0 ? Math.ceil(totalElements / pageSize) : 0;

const getCurrentUserIdValue = (): string | undefined => Session.loadFromStorage(() => {}).user?.id?.value;

const getRelatedConnectionUserProfile = (payload: unknown): UserProfileJSON | undefined => {
  const currentUserId = getCurrentUserIdValue();
  const userA = getPayloadUserProfile(payload, "userA");
  const userB = getPayloadUserProfile(payload, "userB");

  if (currentUserId === userA?.id) return userB;
  if (currentUserId === userB?.id) return userA;

  return userA ?? userB;
};

const buildNetworkConnectionInfoFromPayload = (
  payload: unknown,
  state: RootState,
  existing?: UserInformationJSON,
): UserInformationJSON | undefined => {
  const providedConnectionInfo = toUserInformationJSON(payload);
  if (providedConnectionInfo) return providedConnectionInfo;

  const connectionId = getPayloadId(payload);
  const relatedUser = getRelatedConnectionUserProfile(payload);
  const individual = getIndividualByUserId(state, relatedUser?.id) ?? getIndividualByUserId(state, existing?.id);
  if (!connectionId || !relatedUser || !individual) return undefined;

  return {
    ...existing,
    id: relatedUser.id,
    hasSigningAuthority: relatedUser.hasSigningAuthority ?? existing?.hasSigningAuthority ?? false,
    email: relatedUser.email ?? existing?.email ?? individual.email ?? "",
    firstName: relatedUser.firstName ?? existing?.firstName ?? individual.firstName,
    lastName: relatedUser.lastName ?? existing?.lastName ?? individual.lastName,
    connectionId,
    createdDate: (payload as { createdDate?: string }).createdDate ?? existing?.createdDate,
    individualId: individual.id,
    companyId: individual.companyId ?? existing?.companyId,
    legalEntityId: individual.legalEntityId,
    companyName: individual.companyName ?? existing?.companyName,
    companyProfileVisible: individual.isVisible ?? existing?.companyProfileVisible,
    avatarId: individual.avatarId ?? existing?.avatarId,
  };
};

const shouldConnectionBeInQuery = (connection: UserInformationJSON, networkConnectionQuery: NetworkConnectionQuery): boolean => {
  const query = networkConnectionQuery.query;
  if (!query) return true;

  return (
    textIncludes(connection.firstName, query) ||
    textIncludes(connection.lastName, query) ||
    textIncludes(`${connection.firstName} ${connection.lastName}`, query) ||
    textIncludes(connection.email, query) ||
    textIncludes(connection.companyName, query)
  );
};

/**
 * Patches each cached answer that shows this connection. Returns false when a cached list page
 * does not show it yet: the server pages and orders the list, so only a refetch can say where a
 * new connection goes.
 */
const patchNetworkConnectionCaches = (
  payload: unknown,
  dispatch: AppDispatch,
  getState: () => RootState,
): boolean => {
  const tags = buildNetworkConnectionTags(payload);
  const connectionId = getPayloadId(payload);
  const relatedUserId = getRelatedUserId(payload, "userA", "userB");
  let isOnEveryCachedPage = true;
  let upsertedConnection = buildNetworkConnectionInfoFromPayload(payload, getState());

  for (const { originalArgs } of getCachedQueryEntries(getState, tags, "getUserNetworkConnections")) {
    dispatch(
      userNetworkConnectionsApi.util.updateQueryData(
        "getUserNetworkConnections",
        originalArgs as NetworkConnectionListArgs,
        (draft) => {
          const index = draft.data.findIndex(
            (connection) => connection.connectionId === connectionId || connection.id === relatedUserId,
          );
          const existing = index >= 0 ? draft.data[index] : undefined;
          const connection = buildNetworkConnectionInfoFromPayload(payload, getState(), existing) ?? upsertedConnection;
          upsertedConnection = connection;
          if (!connection) return;

          const isInQuery = shouldConnectionBeInQuery(connection, (originalArgs as NetworkConnectionListArgs).query);
          if (index < 0) {
            if (isInQuery) isOnEveryCachedPage = false;
            return;
          }
          if (isInQuery) {
            draft.data[index] = connection;
          } else {
            draft.data.splice(index, 1);
            draft.headers.totalElements = Math.max(0, draft.headers.totalElements - 1);
            draft.headers.totalPages = getTotalPages(draft.headers.totalElements, draft.headers.pageSize);
          }
        },
      ),
    );
  }

  if (!upsertedConnection) {
    // The payload names the person but not enough to store the connection. Make the status ask
    // the server again: nothing else would, because the bulk status request holds no subscription.
    if (relatedUserId) dispatch(forgetNetworkConnectionStatus(relatedUserId));
    return false;
  }

  dispatch(upsertNetworkConnection(upsertedConnection));
  return isOnEveryCachedPage;
};

const removeNetworkConnectionFromCaches = (
  payload: unknown,
  dispatch: AppDispatch,
  getState: () => RootState,
): void => {
  const tags = buildNetworkConnectionTags(payload);
  const connectionId = getPayloadId(payload);
  const relatedUserId = getRelatedUserId(payload, "userA", "userB");

  for (const { originalArgs } of getCachedQueryEntries(getState, tags, "getUserNetworkConnections")) {
    dispatch(
      userNetworkConnectionsApi.util.updateQueryData(
        "getUserNetworkConnections",
        originalArgs as NetworkConnectionListArgs,
        (draft) => {
          const originalLength = draft.data.length;
          draft.data = draft.data.filter(
            (connection) => connection.connectionId !== connectionId && connection.id !== relatedUserId,
          );
          if (draft.data.length !== originalLength) {
            draft.headers.totalElements = Math.max(0, draft.headers.totalElements - 1);
            draft.headers.totalPages = getTotalPages(draft.headers.totalElements, draft.headers.pageSize);
          }
        },
      ),
    );
  }

  dispatch(removeNetworkConnection({ connectionId, userId: relatedUserId }));
};

const isAcceptedInvitation = (payload: unknown): boolean => {
  if (!payload || typeof payload !== "object") return false;
  const status = (payload as { status?: unknown }).status;
  return typeof status === "string" && status.toLowerCase() === "accepted";
};

const getInvitationContactProfile = (invitation: NetworkInvitationInfoJSON): UserProfileJSON =>
  invitation.isInviter ? invitation.invitee : invitation.inviter;

const buildNetworkInvitationInfoFromPayload = (
  payload: unknown,
  state: RootState,
  existing?: NetworkInvitationInfoJSON,
): NetworkInvitationInfoJSON | undefined => {
  const providedInvitationInfo = toNetworkInvitationInfoJSON(payload);
  if (providedInvitationInfo) return providedInvitationInfo;

  const id = getPayloadId(payload);
  const inviter = getPayloadUserProfile(payload, "inviter") ?? existing?.inviter;
  const invitee = getPayloadUserProfile(payload, "invitee") ?? existing?.invitee;
  const status = (payload as { status?: unknown } | undefined)?.status;
  const created = (payload as { created?: unknown } | undefined)?.created;
  const message = (payload as { message?: unknown } | undefined)?.message;
  const individual = getIndividualByUserId(state, invitee?.id);
  const currentUserId = getCurrentUserIdValue();
  const individualId = existing?.individualId ?? individual?.id;

  if (!id || !inviter || !invitee || typeof status !== "string" || typeof created !== "string" || !individualId) {
    return undefined;
  }

  return {
    id,
    status,
    isInviter: currentUserId ? currentUserId === inviter.id : existing?.isInviter ?? false,
    individualId,
    avatarId: existing?.avatarId ?? individual?.avatarId,
    invitee,
    inviter,
    created,
    message: typeof message === "string" ? message : existing?.message,
  };
};

const matchesInvitationDirection = (invitation: NetworkInvitationInfoJSON, args: unknown): boolean => {
  const direction = (args as NetworkInvitationQuery | undefined)?.filterParameters?.direction;
  if (direction === NetworkInvitationDirection.Received) return !invitation.isInviter;
  if (direction === NetworkInvitationDirection.Sent) return invitation.isInviter;
  return true;
};

/** Whether the server counts this invitation in the query's total, on whichever page it falls. */
const matchesInvitationQuery = (invitation: NetworkInvitationInfoJSON, args: unknown): boolean => {
  const query = args as NetworkInvitationQuery | undefined;
  if (!query) return true;

  const { filterParameters } = query;
  if (!filterParameters?.includeRejected && invitation.status.toLowerCase() === "rejected") return false;
  if (filterParameters?.status && invitation.status.toLowerCase() !== filterParameters.status.toLowerCase()) {
    return false;
  }
  if (!matchesInvitationDirection(invitation, args)) return false;

  const contact = getInvitationContactProfile(invitation);
  const contactName = `${contact.firstName} ${contact.lastName}`;
  if (filterParameters?.name && !textIncludes(contactName, filterParameters.name)) return false;
  if (filterParameters?.message && !textIncludes(invitation.message, filterParameters.message)) return false;
  if (query.query) {
    return (
      textIncludes(contact.firstName, query.query) ||
      textIncludes(contact.lastName, query.query) ||
      textIncludes(contactName, query.query) ||
      textIncludes(invitation.message, query.query) ||
      textIncludes(invitation.status, query.query)
    );
  }

  return true;
};

/** A plain copy of a cached row, which outlives the draft it was read from. */
const snapshot = (invitation: NetworkInvitationInfoJSON): NetworkInvitationInfoJSON =>
  isDraft(invitation) ? current(invitation) : invitation;

/**
 * Patches each cached page that shows this invitation. Returns false when some other cached page's
 * total may have changed: the server pages and counts the list, so an invitation that joins or
 * leaves a query (an acceptance taking it out of Open, say) moves every page's footer, and only a
 * refetch can say where it now falls.
 */
const patchNetworkInvitationCaches = (
  payload: unknown,
  dispatch: AppDispatch,
  getState: () => RootState,
  { isNew }: { isNew: boolean },
): boolean => {
  const tags = buildNetworkInvitationTags(payload);
  const invitationId = getPayloadId(payload);
  let upsertedInvitation = buildNetworkInvitationInfoFromPayload(payload, getState());
  let previousInvitation: NetworkInvitationInfoJSON | undefined;
  const pagesWithout: unknown[] = [];

  for (const { originalArgs } of getCachedQueryEntries(getState, tags, "getUserNetworkInvitations")) {
    dispatch(
      userNetworkInvitationApi.util.updateQueryData(
        "getUserNetworkInvitations",
        originalArgs as NetworkInvitationQuery,
        (draft: Paginated<NetworkInvitationInfoJSON>) => {
          const index = draft.data.findIndex((invitation) => invitation.id === invitationId);
          if (index < 0) {
            pagesWithout.push(originalArgs);
            return;
          }

          const existing = snapshot(draft.data[index]);
          previousInvitation ??= existing;
          const invitation = buildNetworkInvitationInfoFromPayload(payload, getState(), existing) ?? upsertedInvitation;
          upsertedInvitation = invitation;
          if (!invitation) return;

          if (matchesInvitationQuery(invitation, originalArgs)) {
            draft.data[index] = invitation;
          } else {
            draft.data.splice(index, 1);
            draft.headers.totalElements = Math.max(0, draft.headers.totalElements - 1);
            draft.headers.totalPages = getTotalPages(draft.headers.totalElements, draft.headers.pageSize);
          }
        },
      ),
    );
  }

  if (!upsertedInvitation) return false;
  dispatch(upsertNetworkInvitation(upsertedInvitation));

  const invitation = upsertedInvitation;
  const previous = previousInvitation;
  // Before this change the invitation was counted wherever its cached copy matched. A new one was
  // counted nowhere. An update that no cached page shows could have been counted anywhere its
  // direction allows, the direction being the one thing an invitation never changes.
  const wasCounted = (args: unknown): boolean => {
    if (previous) return matchesInvitationQuery(previous, args);
    return !isNew && matchesInvitationDirection(invitation, args);
  };
  return !pagesWithout.some((args) => matchesInvitationQuery(invitation, args) || wasCounted(args));
};

/** Takes the invitation off the cached pages that show it; returns false when other pages counted it. */
const removeNetworkInvitationFromCaches = (
  payload: unknown,
  dispatch: AppDispatch,
  getState: () => RootState,
): boolean => {
  const tags = buildNetworkInvitationTags(payload);
  const invitationId = getPayloadId(payload);
  if (!invitationId) return true;
  let previousInvitation: NetworkInvitationInfoJSON | undefined;
  const pagesWithout: unknown[] = [];

  for (const { originalArgs } of getCachedQueryEntries(getState, tags, "getUserNetworkInvitations")) {
    dispatch(
      userNetworkInvitationApi.util.updateQueryData(
        "getUserNetworkInvitations",
        originalArgs as NetworkInvitationQuery,
        (draft: Paginated<NetworkInvitationInfoJSON>) => {
          const index = draft.data.findIndex((invitation) => invitation.id === invitationId);
          if (index < 0) {
            pagesWithout.push(originalArgs);
            return;
          }
          previousInvitation ??= snapshot(draft.data[index]);
          draft.data.splice(index, 1);
          draft.headers.totalElements = Math.max(0, draft.headers.totalElements - 1);
          draft.headers.totalPages = getTotalPages(draft.headers.totalElements, draft.headers.pageSize);
        },
      ),
    );
  }

  dispatch(removeNetworkInvitation({ invitationId }));
  const removed = previousInvitation;
  return !removed || !pagesWithout.some((args) => matchesInvitationQuery(removed, args));
};

const networkConnectionCallbacks = withCallbacks<RootState, AppDispatch>()
  .add("network-connection-created", (payload: unknown) => (dispatch, getState) => {
    if (patchNetworkConnectionCaches(payload, dispatch, getState)) return;
    dispatch(userNetworkConnectionsApi.util.invalidateTags(buildNetworkConnectionTags(payload)));
  })
  .add("network-connection-updated", (payload: unknown) => (dispatch, getState) => {
    if (patchNetworkConnectionCaches(payload, dispatch, getState)) return;
    dispatch(userNetworkConnectionsApi.util.invalidateTags(buildNetworkConnectionTags(payload)));
  })
  .add("network-connection-deleted", (payload: unknown) => (dispatch, getState) => {
    removeNetworkConnectionFromCaches(payload, dispatch, getState);
  });

const networkInvitationCallbacks = withCallbacks<RootState, AppDispatch>()
  .add("network-invitation-created", (payload: unknown) => (dispatch, getState) => {
    if (patchNetworkInvitationCaches(payload, dispatch, getState, { isNew: true })) return;
    dispatch(userNetworkInvitationApi.util.invalidateTags(buildNetworkInvitationTags(payload)));
  })
  .add("network-invitation-updated", (payload: unknown) => (dispatch, getState) => {
    if (!patchNetworkInvitationCaches(payload, dispatch, getState, { isNew: false })) {
      dispatch(userNetworkInvitationApi.util.invalidateTags(buildNetworkInvitationTags(payload)));
    }

    if (isAcceptedInvitation(payload)) {
      dispatch(userNetworkConnectionsApi.util.invalidateTags(buildNetworkConnectionTagsFromInvitation(payload)));
    }
  })
  .add("network-invitation-deleted", (payload: unknown) => (dispatch, getState) => {
    if (removeNetworkInvitationFromCaches(payload, dispatch, getState)) return;
    dispatch(userNetworkInvitationApi.util.invalidateTags(buildNetworkInvitationTags(payload)));
  });

export const networkConnectionsAblyReduxMiddleware = reduxAblyMiddleware<RootState, AppDispatch>({
  callbacks: networkConnectionCallbacks,
  channelName: ablyChannelNamesForSession.networkConnections,
  connectionHealth: { key: "network-connections", label: "Network Connections" },
  shouldConnectionStartImmediately: true,
});

export const networkInvitationsAblyReduxMiddleware = reduxAblyMiddleware<RootState, AppDispatch>({
  callbacks: networkInvitationCallbacks,
  channelName: ablyChannelNamesForSession.networkInvitations,
  connectionHealth: { key: "network-invitations", label: "Network Invitations" },
  shouldConnectionStartImmediately: true,
});

